import React, { useEffect, useState } from "react";
import useAuth from '../../../hooks/useAuth';
import GearSpinner from '../../../components/GearSpinner/GearSpinner';
import Loader from '../../../components/Loader/Loader';
import logo from '../../../assets/images/big-logo.jpg';
import printIcon from '../../../assets/images/print-img.png';
import editIcon from '../../../assets/images/edit-icon.png';
import shareIcon from '../../../assets/images/share-img.png';
import pdfIcon from '../../../assets/images/pdf-img.png';
import searchIcon from '../../../assets/images/search-icon.png';
import xlsIcon from '../../../assets/images/xls-img.png';
import QuoteMeService from "../../../services/QuoteMeService";
import ShareQuoteMeModal from '../../../components/Modals/ShareQuoteMeModal';
import EditQuoteSalesTable from "../EditQuoteSalesTable";
import AlertMessage from "../../../components/AlertMessage/AlertMessage";
import '../print.css';
import moment from "moment-timezone";
import { formatUSD } from '../../../utils/common';
import { Validation } from "../../../utils/validation";

const QuoteHistoryTab = ({ active }) => {
    const { user } = useAuth();
    const [detailLoader, setdetailLoader] = React.useState(false);
    const [historyLoader, setHistoryLoader] = React.useState(true);
    const [historyData, setHistoryData] = React.useState([]);
    const [quoteDetails, setQuoteDetails] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [formPayload, setFormPayload] = React.useState({});
    const [showLoader, setShowLoader] = React.useState(false);
    const [showShareModal, setShowShareModal] = React.useState(false);
    const [isEdit, setIsEdit] = React.useState(false);
    const [products, setProducts] = useState([]);
    const [productMap, setProductMap] = useState({});
    const [multiplierMap, setMultiplierMap] = useState({});
    const [multiplierOptions, setMultiplierOptions] = useState([]);
    const [rowErrors, setRowErrors] = useState({});
    const [isCustomer, setIsCustomer] = useState(false);
    const [isProducts, setIsProducts] = useState(false);
    const [estTime, setEstTime] = useState("");
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [showHistory, setShowHistory] = useState(true);

    const quoteMeService = new QuoteMeService();

    const parseNumber = (value) => {
        if (value === null || value === undefined || value === "") return 0;
        const num = Number(String(value).replace(/[^0-9.-]+/g, ""));
        return isNaN(num) ? 0 : num;
    };

    useEffect(() => {
        if (active) {
            getQuoteHistory();
        }
        setEstTime(
            moment()
                .tz("America/New_York")
                .format("MMMM DD, YYYY hh:mm:ss A z")
        );
    }, [active]);

    useEffect(() => {
        let data = { "portal_user_name": user.name }
        if(quoteDetails) {
            if (isCustomer) {
                data['company_name'] = formPayload.customer_name
            }
            quoteMeService.getCustomerMultipliers(data).then((response) => {
                const data = response.data;
                if (data.status === "success") {
                    if (isCustomer) {
                        const mMap = {};
                        data.result.customers.multipliers.forEach(m => {
                            mMap[m.multiplier_group] = Number(m.customer_multiplier);
                        });
                        setMultiplierMap(mMap);

                        const rawProducts = Array.isArray(data.result.products)
                            ? data.result.products
                            : Object.values(data.result.products).flat();

                        const pMap = {};
                        const productOptions = [];
                        rawProducts.forEach(p => {
                            pMap[p.num] = p;
                            productOptions.push({ value: p.num, label: p.num });
                        });

                        if (rawProducts.length > 0) setIsProducts(true);
                        setProductMap(pMap);
                        setProducts(productOptions);
                    } else {
                        const mMap = {};
                        const mOptionsMap = {};

                        data.result.customers.forEach(customer => {
                            customer.multipliers.forEach(m => {
                                const val = Number(m.customer_multiplier);
                                if (!Number.isFinite(val)) return;
                                if (!mOptionsMap[val] && val > 0) {
                                    mOptionsMap[val] = { value: val, label: val.toFixed(3) };
                                }
                                mMap[val] = val;
                            });
                        });

                        const sortedMultiplierOptions = Object.values(mOptionsMap).sort(
                            (a, b) => Number(a.value) - Number(b.value)
                        );

                        setMultiplierMap(mMap);
                        setMultiplierOptions(Object.values(sortedMultiplierOptions));

                        const rawProducts = Array.isArray(data.result.products)
                            ? data.result.products
                            : Object.values(data.result.products).flat();

                        const pMap = {};
                        const productOptions = [];
                        rawProducts.forEach(p => {
                            pMap[p.num] = p;
                            productOptions.push({ value: p.num, label: p.num });
                        });

                        if (rawProducts.length > 0) setIsProducts(true);
                        setProductMap(pMap);
                        setProducts(productOptions);
                    }
                }
            }).catch(error => {
                console.error('Error fetching multipliers:', error);
            });
        }
    }, [isEdit]);

    if (!active) return null;

    const getQuoteHistory = async () => {
        quoteMeService.getQuoteHistory({ portal_user_id: user.id }).then((response) => {
            setHistoryLoader(false);
            if (response.data.status === 'success') {
                setHistoryData(response.data.result);
            }
        }).catch((error) => {
            console.log("Error fetching quote history:", error);
        })
    }

    const getQuoteDetails = async (orderId) => {
        setdetailLoader(true);
        setIsEdit(false);
        quoteMeService.getQuoteDetails({ portal_user_id: user.id, order_id: orderId }).then((response) => {
            setdetailLoader(false);
            if (response.data.status === 'success') {
                setIsCustomer(!!(response.data.result.order.customer_name));
                setQuoteDetails(response.data.result);
                buildPayload(response.data.result);
            }
        }).catch((error) => {
            console.log("Error fetching quote details:", error);
        });
    }

    const buildPayload = (data) => {
        const { order, items } = data || {};
        setFormPayload({
            id: order.id,
            portal_user_id: order.portal_user_id,
            order_number: order?.order_number || "",
            order_date: order?.order_date || "",
            date_schedule: order?.date_schedule || "",
            customer_name: order?.customer_name || "",
            rep: order?.rep || "",
            payment_terms: order?.payment_terms || "",
            fob_point: order?.fob_point || "",
            carrier: order?.carrier || "",
            ship_service: order?.ship_service || "",
            billing_address: order?.billing_address || "",
            shipping_address: order?.shipping_address || "",
            notes: order?.notes || "",
            sub_total: parseNumber(order?.sub_total) || "0",
            sales_tax: parseNumber(order?.sales_tax) || "0",
            total: parseNumber(order?.total) || "0",
            sales_data: (items || []).map(item => ({
                type: item.item_type || "Sale",
                product_number: item.product_number || "",
                description: item.description || "",
                unit_price: item.unit_price || "0",
                quantity: item.quantity || 0,
                total_price: item.total_price || "0",
                multiplier: item.multiplier ?? null
            })),
            est_timestamp: order?.est_timestamp || ""
        });
    };

    const filteredHistoryData = historyData.filter((item) => {
        const term = searchTerm.toLowerCase();
        return (
            item.order_number?.toLowerCase().includes(term) ||
            item.order_date?.toLowerCase().includes(term)
        );
    });

    const handleShare = async (email, quote_note) => {
        setShowLoader(true);
        formPayload['sub_total'] = formatUSD(formPayload['sub_total']);
        formPayload['total'] = formatUSD(formPayload['total']);
        try {
            await quoteMeService.shareQuote({
                ...formPayload,
                email_to: [email],
                quote_note: quote_note,
                format: 'both'
            });
        } finally {
            setShowLoader(false);
        }
    };

    const validateSalesRows = () => {
        let currentErrors = {};
        console.log("Validating sales rows with payload:", formPayload);
        formPayload.sales_data.forEach((row, index) => {
            const errors = isCustomer ? Validation(row, ['product_number', 'quantity']) : Validation(row, ['product_number', 'quantity', 'multiplier']);
            if (errors && Object.keys(errors).length > 0) {
                currentErrors[index] = errors;
            }
        });
        setRowErrors(currentErrors);
        return Object.keys(currentErrors).length === 0;
    };

    const handleUdpate = async () => {
        if (!validateSalesRows()) return;
        setShowLoader(true);
        await updateQuote();
    }

    const updateQuote = async () => {
        const payload = {
            ...formPayload,
            date_schedule: formPayload.date_schedule ? moment(formPayload.date_schedule).format('YYYY-MM-DD') : "",
            order_date: formPayload.order_date ? moment(formPayload.order_date).format('YYYY-MM-DD') : "",
            est_timestamp: estTime,
            sales_data: formPayload.sales_data.map(item => ({
                ...item,
                total_price: parseNumber(item.total_price),
                unit_price: parseNumber(item.unit_price)
            }))
        };
        try {
            const response = await quoteMeService.updateQuoteData(payload);
            if (response.data.status !== 'success') {
                setAlert({ type: 'error', message: response.data.message });
                throw new Error(response.data.message);
            }
            setAlert({ type: 'success', message: response.data.message });
            await getQuoteDetails(payload.id);
            setIsEdit(false);
            setShowHistory(true);
        } catch (error) {
            setAlert({ type: 'error', message: error.message || 'Update failed' });
        } finally {
            setShowLoader(false);
        }
    }

    const renderHistoryTable = () => (
        <table className="table text-center techtop-quote-table2">
            <thead>
                <tr>
                    <th style={{ borderRight: "2px solid #fa4616" }}>Name</th>
                    <th>Date</th>
                </tr>
            </thead>
            <tbody>
                {historyLoader ? (
                    <tr><td colSpan="2"><GearSpinner /></td></tr>
                ) : filteredHistoryData.length === 0 ? (
                    <tr><td colSpan="2">No matching records found.</td></tr>
                ) : filteredHistoryData.map((item, index) => (
                    <tr key={index}>
                        <td style={{ borderRight: "2px solid #fa4616" }}>
                            <button className="btn-link-style-2" onClick={() => getQuoteDetails(item.id)}>
                                {item.order_number}
                            </button>
                        </td>
                        <td>{item.order_date}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderCustomerInfo = () => {
        if (!quoteDetails?.order?.customer_name) return null;
        return (
            <div className="row">
                <div className="col-md-12">
                    <div className="table-responsive">
                        <table className="table table-bordered techtop-quote-table text-center">
                            <thead>
                                <tr>
                                    <th>Sales Rep</th>
                                    <th>Payment Terms</th>
                                    <th>FOB Point</th>
                                    <th>Carrier</th>
                                    <th>Ship Service</th>
                                    <th>Date Scheduled</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{quoteDetails.order.rep}</td>
                                    <td>{quoteDetails.order.payment_terms}</td>
                                    <td>{quoteDetails.order.fob_point}</td>
                                    <td>{quoteDetails.order.carrier}</td>
                                    <td>{quoteDetails.order.ship_service}</td>
                                    <td>{quoteDetails.order.date_schedule}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    const renderSalesData = () => (
        <div className="row">
            <div className="col-md-12">
                <div className="table-responsive">
                    {isEdit ? (
                        <EditQuoteSalesTable
                            salesData={formPayload.sales_data}
                            setFormData={setFormPayload}
                            products={products}
                            productMap={productMap}
                            multiplierMap={multiplierMap}
                            multiplierOptions={multiplierOptions}
                            isCustomer={isCustomer}
                            rowErrors={rowErrors}
                        />
                    ) : (
                        <table className="table table-bordered techtop-quote-table2">
                            <thead>
                                <tr>
                                    <th className="text-center">Item #</th>
                                    <th className="text-center">Type</th>
                                    <th className="text-center" style={{ width: "20%" }}>Number</th>
                                    <th className="text-center">Description</th>
                                    <th className="text-center">Unit Price</th>
                                    <th className="text-center">Qty Ordered</th>
                                    {!isCustomer && <th className="text-center">Multiplier</th>}
                                    <th className="text-center">Total Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quoteDetails.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td className="text-center">{item.item_type}</td>
                                        <td>{item.product_number}</td>
                                        <td>{item.description}</td>
                                        <td className="text-center">${item.unit_price}</td>
                                        <td className="text-center">{item.quantity}</td>
                                        {!isCustomer && <td className="text-center">{item.multiplier}</td>}
                                        <td className="text-center">${item.total_price}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="table-responsive my-5 quote-history-div">
                    <table className="table table-bordered techtop-quote-table2">
                        <tbody>
                            <tr>
                                <td rowSpan="3" style={{ width: "70%" }}>
                                    <strong>Notes:</strong>
                                    <div className="notes-text">{quoteDetails.order.notes}</div>
                                </td>
                                <td className="text-end"><strong>Subtotal:</strong></td>
                                <td className="text-end"><strong>${isEdit ? formatUSD(formPayload.sub_total) : quoteDetails.order.sub_total}</strong></td>
                            </tr>
                            <tr>
                                <td className="text-end"><strong>Sales Tax:</strong></td>
                                <td className="text-end"><strong>${isEdit ? formatUSD(formPayload.sales_tax) : quoteDetails.order.sales_tax}</strong></td>
                            </tr>
                            <tr>
                                <td className="text-end"><strong>Total:</strong></td>
                                <td className="text-end"><strong>${isEdit ? formatUSD(formPayload.total) : quoteDetails.order.total}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p>{isEdit ? estTime : quoteDetails.order.est_timestamp}</p>
            </div>
        </div>
    );

    const renderButtons = () => isEdit && (
        <div className="row">
            <div className="d-flex w-100 justify-content-evenly gap-4">
                <button type="button" className="submit-button w-50" onClick={handleUdpate}>Update</button>
                <button type="button" className="btn btn-secondary w-50" onClick={handleResetEdit}>Cancel</button>
            </div>
        </div>
    );

    const renderDefaultDetails = () => (
        <div className="text-center my-5 fade-in">
            <div className="p-5 rounded-3 shadow-sm bg-light border d-inline-block">
                <h3 className="fw-bold text-dark">No Quote Order Selected</h3>
                <p className="text-muted mb-0">Please <span className="fw-semibold">select a quote</span> from the left to view details.</p>
            </div>
        </div>
    );

    const renderQuoteDetails = () => (
        <React.Fragment>
            <div className="row">
                <div className="col-md-12">
                    <div className="section-div1">
                        <div className="row">
                            <div className="col-md-8">
                                <img src={logo} alt="Techtop" />
                                <h3>Techtop Industries, Inc.</h3>
                                <p className="m-0 text-20">2815 Colonnades Court, Peachtree Corners, GA 30071</p>
                            </div>
                            <div className="col-md-4">
                                <h3 className="text-center">Quote</h3>
                                <table className="table table-bordered quote-table text-center">
                                    <thead><tr><th>Order #</th><th>Date</th></tr></thead>
                                    <tbody><tr><td>{quoteDetails.order.order_number}</td><td>{quoteDetails.order.order_date}</td></tr></tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row pt-3">
                <div className="col-md-6">
                    <table className="table table-bordered">
                        <thead><tr><th>Bill To:</th></tr></thead>
                        <tbody><tr><td>{quoteDetails.order.billing_address}</td></tr></tbody>
                    </table>
                    {quoteDetails.order.customer_name && (
                        <table className="table table-bordered equal-table">
                            <tbody><tr><td>Customer: {quoteDetails.order.customer_name}</td></tr></tbody>
                        </table>
                    )}
                </div>
                <div className="col-md-6">
                    <table className="table table-bordered">
                        <thead><tr><th>Ship To: US</th></tr></thead>
                        <tbody><tr><td>{quoteDetails.order.shipping_address}</td></tr></tbody>
                    </table>
                </div>
            </div>
            {renderCustomerInfo()}
            {renderSalesData()}
            {renderButtons()}
        </React.Fragment>
    );

    const exportPdf = () => {
        setShowLoader(true);
        let exportPayload = { 
            ...formPayload,
            sub_total: formatUSD(formPayload.sub_total),
            total: formatUSD(formPayload.total),
            sales_tax: formatUSD(formPayload.sales_tax)
        };
        quoteMeService.exportQuoteOrderPDF(exportPayload).then(response => {
            setShowLoader(false);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `quote-${exportPayload.order_number}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        }).catch(() => setShowLoader(false));
    };

    const exportExcel = () => {
        setShowLoader(true);
        let exportPayload = { 
            ...formPayload,
            sub_total: formatUSD(formPayload.sub_total),
            total: formatUSD(formPayload.total),
            sales_tax: formatUSD(formPayload.sales_tax)
        };
        quoteMeService.exportQuoteOrderExcel(exportPayload).then(response => {
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `quote-${exportPayload.order_number}.xlsx`;
            link.click();
            setShowLoader(false);
        }).catch(() => setShowLoader(false));
    };

    const handleShareClick = () => setShowShareModal(true);
    const handlePrint = () => window.print();
    const handleEdit = () => { 
        // Reset payload to original details before opening edit mode
        buildPayload(quoteDetails); 
        setIsEdit(true); 
        setShowHistory(false); 
    }
    
    const handleResetEdit = () => { 
        setIsEdit(false); 
        setShowHistory(true); 
        // Optional: Reset payload here as well to be safe
        buildPayload(quoteDetails);
    }

    return (
        <React.Fragment>
            <style>
                {`
                .history-toggle-btn {
                    position: absolute;
                    top: 150px;
                    left: ${showHistory ? 'calc(25% + 15px)' : '0px'};
                    transform: translateX(-50%);
                    z-index: 1050;
                    background: #fa4616;
                    color: white;
                    border: 1px solid #d33a12;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                }
                .history-toggle-btn:hover { background: #d33a12; scale: 1.1; }
                .sidebar-col { 
                    transition: all 0.3s ease; 
                    ${!showHistory ? 'width: 0; opacity: 0; padding: 0; margin: 0; overflow: hidden;' : 'width: 25%;'}
                }
                .content-col { transition: all 0.3s ease; }
                @media print { .history-toggle-btn { display: none !important; } }
                `}
            </style>

            <div className="container tab-pane active" id="quote-history" style={{ position: 'relative' }}>
                <div className="row flex-nowrap g-0">
                    {isEdit && (
                        <button 
                            className="history-toggle-btn" 
                            onClick={() => setShowHistory(!showHistory)}
                            type="button"
                        >
                            {showHistory ? '❮' : '❯'}
                        </button>
                    )}

                    <div className="sidebar-col">
                        <div style={{ minWidth: '250px', padding: '0 15px' }}>
                            <h5 className="text-center dark-color1">Previous Quotes</h5>
                            <div className="input-group mb-3">
                                <button className="btn btn-outline-secondary" type="button"><img src={searchIcon} alt="search" /></button>
                                <input type="text" className="form-control" placeholder="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </div>
                            <div className="techtop-quote-list-div">{renderHistoryTable()}</div>
                        </div>
                    </div>

                    {/* Middle Class Logic updated: col-md-12 when history hidden, col-md-7 when shown */}
                    <div className={`content-col print-area ${!showHistory ? 'col-md-12' : (isEdit || !detailLoader && !quoteDetails) ?  'col-md-9' : 'col-md-7' }`} id="printable" style={{ padding: '0 20px' }}>
                        <AlertMessage
                            type={alert.type}
                            message={alert.message}
                            onClose={() => setAlert({ type: '', message: '' })}
                        />
                        <Loader isLoading={detailLoader} message="Processing..." />
                        {!detailLoader && !quoteDetails && renderDefaultDetails()}
                        {!detailLoader && quoteDetails && renderQuoteDetails()}
                    </div>

                    {/* Right column (Buttons) - logic remains. Note: col-md-12 will push this down in a normal row */}
                    {!detailLoader && quoteDetails && !isEdit && (
                        <div className="col-md-2">
                            <ul className="net-pricing-report-list">
                                <li><button className="btn-link-style" onClick={handleEdit}><img src={editIcon} alt="Edit" /> Edit</button></li>
                                <li><button className="btn-link-style" onClick={handlePrint}><img src={printIcon} alt="Print" /> Print</button></li>
                                <li><button className="btn-link-style" onClick={handleShareClick}><img src={shareIcon} alt="Share" /> Share</button></li>
                                <li><button className="btn-link-style" onClick={exportPdf}><img src={pdfIcon} alt="PDF" /> Export PDF</button></li>
                                <li><button className="btn-link-style" onClick={exportExcel}><img src={xlsIcon} alt="Excel" /> Export Excel</button></li>
                            </ul>
                        </div>
                    )}
                </div>

                <Loader isLoading={showLoader} message="Processing..." />
                <ShareQuoteMeModal isShow={showShareModal} onClose={() => setShowShareModal(false)} onShare={handleShare} />
            </div>
        </React.Fragment>
    );
}

export default QuoteHistoryTab;